import { Box, FormControl, MenuItem, Select } from '@mui/material';
import type { SelectChangeEvent } from '@mui/material/Select';
import { useCallback, useMemo, useRef, useState } from 'react';
import { chartPanelSelectMenuProps, chartPanelSelectSx } from '../styles/chartReader.styles';
import { FindDataPointSectionTitleTypography, HintTypography, LabelTypography } from '../styles/chartReader.typography';
import { focusSelectTrigger } from '../utils/selectFocus';
import { ChartPanelSubmitButton } from './ChartPanelSubmitButton';

type SnapshotValueSelectorProps<T> = {
    /** Snapshot values to choose from. Order is irrelevant — sorted internally
     *  by `getKey`, ascending. */
    values: T[];
    /** The string that both identifies and displays a value — e.g. a sector
     *  name or a credit grade. Assumed unique across `values`. */
    getKey: (value: T) => string;
    /** Prefix for the `<select>` id so multiple snapshot selectors on a page
     *  never collide. */
    idPrefix: string;
    /** Visible label above the dropdown, e.g. "Sector" or "Data Series". */
    fieldLabel: string;
    /** Placeholder shown before a pick is made, e.g. "Select sector". */
    placeholder: string;
    /** aria-label for the dropdown once a value's been picked, e.g. "Sector"
     *  or "Data series" — kept separate from `fieldLabel` since the two don't
     *  always share the same capitalization. */
    selectedAriaLabel: string;
    /** Hint line under the section title. */
    hint: string;
    /** Submit button aria-label while no value is picked yet. */
    disabledSubmitAriaLabel: string;
    /** Submit button aria-label once a value is picked, given its key. */
    readySubmitAriaLabel: (key: string) => string;
    /** Called only when the user presses Submit after picking a value.
     *  Selecting the same value again and re-submitting still fires so the
     *  parent can re-announce. */
    onSubmit: (value: T) => void;
};

/** Screen-reader guidance for `ChartDataSectionShell`'s Submit-triggered
 *  announcement — accurate only because Tab from Submit loops back to the
 *  dropdown (see `handleTabAwayFromSubmit` below) rather than leaving the
 *  panel. Kept next to that behavior so the two can't drift apart silently.
 *  Callers supply the noun matching their own `hint` copy. */
export function submitGuidance(noun: string): string {
    return `Press Tab to select a different ${noun}.`;
}

/**
 * Shared single-dropdown selector for point-in-time chart snapshots keyed by
 * a name string (sector, credit grade, asset type, ...) rather than a date —
 * the counterpart to DataPointSelector's Year/Month/Date pattern. Used by
 * SectorSelector, AssetAllocationSelector, CreditQualitySelector, and
 * SummaryCreditRatingSelector, which are thin wrappers supplying the
 * type-specific copy/aria-label strings. The dropdown pick only updates local
 * state; the parent callback (and the shell's re-announcement) fires from the
 * Submit button (WCAG 2.2 3.2.2 On Input).
 */
export function SnapshotValueSelector<T>({
    values,
    getKey,
    idPrefix,
    fieldLabel,
    placeholder,
    selectedAriaLabel,
    hint,
    disabledSubmitAriaLabel,
    readySubmitAriaLabel,
    onSubmit,
}: SnapshotValueSelectorProps<T>) {
    const [selectedKey, setSelectedKey] = useState<string>('');
    const selectControlRef = useRef<HTMLDivElement>(null);
    const submitButtonRef = useRef<HTMLButtonElement>(null);

    const sortedValues = useMemo(
        () => [...values].sort((a, b) => getKey(a).localeCompare(getKey(b))),
        [values, getKey],
    );

    const handleChange = useCallback((event: SelectChangeEvent<string>) => {
        setSelectedKey(event.target.value);
        // This is the only field in the sequence, so completing it moves focus
        // straight to Submit — delayed so React can flip Submit's disabled
        // state first (disabled buttons can't receive focus).
        setTimeout(() => submitButtonRef.current?.focus(), 50);
    }, []);

    // Submit is the last stop in the selection loop — Tab from here would
    // otherwise leave the panel entirely (e.g. into the page footer), so loop
    // back to the dropdown instead, letting the user immediately start a new pick.
    const handleTabAwayFromSubmit = useCallback(() => {
        focusSelectTrigger(selectControlRef);
    }, []);

    const pendingValue = useMemo(
        () => sortedValues.find((v) => getKey(v) === selectedKey) ?? null,
        [sortedValues, selectedKey, getKey],
    );

    const handleSubmit = useCallback(() => {
        if (pendingValue) onSubmit(pendingValue);
    }, [pendingValue, onSubmit]);

    const submitAriaLabel = pendingValue ? readySubmitAriaLabel(getKey(pendingValue)) : disabledSubmitAriaLabel;

    const selectId = `${idPrefix}-select`;

    return (
        <Box>
            <FindDataPointSectionTitleTypography>Find a specific data point</FindDataPointSectionTitleTypography>
            <HintTypography sx={{ mt: 0.5 }}>{hint}</HintTypography>

            <Box sx={{ mt: 1.5 }}>
                <label htmlFor={selectId}>
                    <LabelTypography sx={{ mb: 0.5 }}>{fieldLabel}</LabelTypography>
                </label>
                <FormControl ref={selectControlRef} fullWidth size='small' sx={chartPanelSelectSx}>
                    <Select<string>
                        id={selectId}
                        value={selectedKey}
                        onChange={handleChange}
                        displayEmpty
                        renderValue={(v) => (v ? v : placeholder)}
                        inputProps={{ 'aria-label': selectedKey === '' ? placeholder : selectedAriaLabel }}
                        MenuProps={chartPanelSelectMenuProps}>
                        {sortedValues.map((v) => (
                            <MenuItem key={getKey(v)} value={getKey(v)}>
                                {getKey(v)}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Box>

            <ChartPanelSubmitButton
                ref={submitButtonRef}
                disabled={!pendingValue}
                ariaLabel={submitAriaLabel}
                onSubmit={handleSubmit}
                onTabAway={handleTabAwayFromSubmit}
            />
        </Box>
    );
}
